<template>
    <div class="poll-create-wizard">
        <!-- Step pills -->
        <div class="poll-wizard-steps">
            <button class="poll-wizard-step-pill" :class="{ 'is-active': step === 1, 'is-done': step > 1 }" type="button" @click="goToStep(1)">
                <span class="step-pill-num">1</span>
                <span class="step-pill-label">Basis</span>
            </button>
            <span class="step-pill-arrow">›</span>
            <button class="poll-wizard-step-pill" :class="{ 'is-active': step === 2, 'is-done': step > 2 }" type="button" @click="goToStep(2)">
                <span class="step-pill-num">2</span>
                <span class="step-pill-label">Datums</span>
            </button>
            <span class="step-pill-arrow">›</span>
            <button class="poll-wizard-step-pill" :class="{ 'is-active': step === 3, 'is-done': step > 3 }" type="button" @click="goToStep(3)">
                <span class="step-pill-num">3</span>
                <span class="step-pill-label">Instellingen</span>
            </button>
            <span class="step-pill-arrow">›</span>
            <button class="poll-wizard-step-pill" :class="{ 'is-active': step === 4 }" type="button" @click="goToStep(4)">
                <span class="step-pill-num">4</span>
                <span class="step-pill-label">Deelnemers</span>
            </button>
        </div>

        <!-- Progress bar -->
        <div class="poll-wizard-progress-bar">
            <span class="poll-wizard-progress-fill" :style="{ width: `${wizardProgress}%` }"></span>
        </div>

        <!-- ── Stap 1: Basis ─────────────────────────────────── -->
        <section v-if="step === 1" class="poll-wizard-panel">
            <div class="step-intro">
                <strong class="step-intro-title">Stap 1 — Basis</strong>
                <p class="muted text-sm m-0">Geef je datumprikker een duidelijke titel en eventueel een korte toelichting.</p>
            </div>

            <div v-if="showCreatorFields" class="form-stack form-stack-xs">
                <div v-if="creatorLocked" class="muted text-sm">
                    Je maakt deze prikker aan als <strong>{{ creatorName }}</strong> ({{ creatorEmail }}).
                </div>
                <div v-else class="form-field-row">
                    <label class="form-field">
                        <span class="form-field-label">Jouw naam *</span>
                        <input v-model="formData.creator_name" :name="htmlFieldNames ? 'creator_name' : null" type="text" required />
                        <div v-if="mergedErrors.creator_name" class="error-box section-spacing-top-xs">
                            {{ mergedErrors.creator_name }}
                        </div>
                    </label>
                    <label class="form-field">
                        <span class="form-field-label">Jouw e-mail *</span>
                        <input v-model="formData.creator_email" :name="htmlFieldNames ? 'creator_email' : null" type="email" required />
                        <div v-if="mergedErrors.creator_email" class="error-box section-spacing-top-xs">
                            {{ mergedErrors.creator_email }}
                        </div>
                    </label>
                </div>
            </div>

            <div class="form-field-row">
                <label class="form-field">
                    <span class="form-field-label">Titel *</span>
                    <input v-model="formData.title" :name="htmlFieldNames ? 'title' : null" type="text" required placeholder="Bijv. Familie BBQ" />
                    <div v-if="mergedErrors.title" class="error-box section-spacing-top-xs">{{ mergedErrors.title }}</div>
                </label>
            </div>
            <div class="form-field-row">
                <label class="form-field">
                    <span class="form-field-label">Beschrijving</span>
                    <input v-model="formData.description" :name="htmlFieldNames ? 'description' : null" type="text" placeholder="Optionele toelichting" />
                </label>
            </div>
        </section>

        <!-- ── Stap 2: Datums ────────────────────────────────── -->
        <section v-if="step === 2" class="poll-wizard-panel">
            <div class="step-intro">
                <strong class="step-intro-title">Stap 2 — Datums</strong>
                <p class="muted text-sm m-0">Kies minimaal 2 datums. Je kunt per datum ook een tijdstip opgeven.</p>
            </div>

            <!-- Hidden form fields for native POST -->
            <template v-if="useDateModal && htmlFieldNames">
                <input v-model="formData.closes_at" type="hidden" name="closes_at" />
                <input
                    v-for="date in sortedSelectedDates"
                    :key="`hidden-option-${date}`"
                    type="hidden"
                    name="options[]"
                    :value="date"
                />
                <template v-for="date in sortedSelectedDates" :key="`hidden-time-${date}`">
                    <input
                        v-if="formData.option_times[date]"
                        type="hidden"
                        :name="`option_times[${date}]`"
                        :value="formData.option_times[date]"
                    />
                </template>
            </template>

            <div class="form-field">
                <div class="poll-dates-header">
                    <span>Datums *</span>
                    <template v-if="useDateModal">
                        <button class="button button-secondary button--xs" type="button" @click="emit('openDateModal')">
                            + Datums kiezen
                        </button>
                    </template>
                </div>

                <!-- Date + time overview table -->
                <template v-if="useDateModal">
                    <div v-if="sortedSelectedDates.length" class="poll-dates-table">
                        <div class="poll-dates-table-head">
                            <span>Datum</span>
                            <span>Tijdstip (optioneel)</span>
                            <span></span>
                        </div>
                        <div
                            v-for="(date, dateIndex) in sortedSelectedDates"
                            :key="`row-${date}`"
                            class="poll-dates-table-row"
                        >
                            <span class="poll-dates-table-date">
                                {{ formatDisplayDate(date) }}
                            </span>
                            <span class="poll-dates-table-time">
                                <input
                                    v-model="formData.option_times[date]"
                                    :name="htmlFieldNames ? `option_times[${date}]` : null"
                                    class="time-input"
                                    type="time"
                                    placeholder="--:--"
                                />
                            </span>
                            <span class="poll-dates-table-apply">
                                <button
                                    v-if="dateIndex === 0 && canApplyFirstTimeToAll"
                                    class="button button-secondary button--xs btn-apply-time"
                                    type="button"
                                    title="Dit tijdstip doorvoeren naar alle datums hieronder"
                                    @click="showApplyConfirm = true"
                                >
                                    Doorvoeren ↓
                                </button>
                            </span>
                        </div>

                        <!-- Inline confirm panel -->
                        <div v-if="showApplyConfirm" class="poll-time-confirm-panel">
                            <div class="poll-time-confirm-text">
                                Tijdstip <strong>{{ firstDateTimeValue }}</strong> instellen voor alle gekozen datums?
                            </div>
                            <div class="poll-time-confirm-actions">
                                <button class="button button-secondary button--xs" type="button" @click="showApplyConfirm = false">
                                    Annuleren
                                </button>
                                <button class="button button-primary button--xs" type="button" @click="confirmApplyTime">
                                    Ja, doorvoeren
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-else class="poll-dates-empty">
                        <span class="muted text-sm">Nog geen datums gekozen. Klik op '+ Datums kiezen' om te beginnen.</span>
                    </div>
                </template>

                <!-- Fallback: fixed date inputs (no modal) -->
                <template v-else>
                    <p class="muted text-xs section-spacing-top-2xs section-spacing-sm">Voeg minimaal 2 datums toe.</p>
                    <div v-for="(date, index) in fixedOptionSlots" :key="`option-${index}`">
                        <input
                            v-model="fixedOptionSlots[index]"
                            :name="htmlFieldNames ? 'options[]' : null"
                            type="date"
                            class="section-spacing-top-xs"
                            @input="syncFixedOptionSlots"
                        />
                    </div>
                </template>

                <div v-if="mergedErrors.options" class="error-box section-spacing-top-xs">
                    {{ mergedErrors.options }}
                </div>
            </div>

            <!-- Closing date -->
            <label class="form-field">
                Sluitdatum (optioneel)
                <small class="muted d-block section-spacing-top-2xs">
                    Na deze datum kan niemand meer stemmen en kun je de uitslag definitief kiezen.
                </small>
                <template v-if="useDateModal">
                    <div class="date-picker-trigger">
                        <button class="button button-ghost button--xs" type="button" @click="emit('openCloseDateModal')">
                            {{ formData.closes_at ? 'Datum wijzigen' : '+ Datum kiezen' }}
                        </button>
                        <span v-if="formData.closes_at" class="muted text-sm closes-at-display">{{ formatReadableDate(formData.closes_at) }}</span>
                    </div>
                </template>
                <template v-else>
                    <input v-model="formData.closes_at" :name="htmlFieldNames ? 'closes_at' : null" type="date" class="section-spacing-top-xs" />
                </template>
            </label>
        </section>

        <!-- ── Stap 3: Instellingen ──────────────────────────── -->
        <section v-if="step === 3" class="poll-wizard-panel">
            <div class="step-intro">
                <strong class="step-intro-title">Stap 3 — Instellingen</strong>
                <p class="muted text-sm m-0">Stel in hoe deelnemers kunnen stemmen en wanneer de uitslag zichtbaar is.</p>
            </div>

            <!-- Visibility mode -->
            <div class="form-field">
                <span>Uitslagen zichtbaar</span>
                <div class="radio-card-group">
                    <label class="radio-card" :class="{ 'is-selected': formData.visibility_mode === 'public_results' }">
                        <input v-model="formData.visibility_mode" :name="htmlFieldNames ? 'visibility_mode' : null" type="radio" value="public_results" />
                        <div class="radio-card-body">
                            <strong>Direct tonen</strong>
                            <small class="muted">Iedereen ziet direct wie op welke datum heeft gestemd.</small>
                        </div>
                    </label>
                    <label class="radio-card" :class="{ 'is-selected': formData.visibility_mode === 'hidden_until_closed' }">
                        <input v-model="formData.visibility_mode" :name="htmlFieldNames ? 'visibility_mode' : null" type="radio" value="hidden_until_closed" />
                        <div class="radio-card-body">
                            <strong>Pas na sluiten</strong>
                            <small class="muted">De uitslag is pas zichtbaar nadat jij de prikker sluit.</small>
                        </div>
                    </label>
                </div>
            </div>

            <!-- Show voter names -->
            <div class="form-field">
                <span>Deelnemers zichtbaar</span>
                <div class="radio-card-group">
                    <label class="radio-card" :class="{ 'is-selected': formData.show_voter_names === true }">
                        <input v-model="formData.show_voter_names" :name="htmlFieldNames ? 'show_voter_names' : null" type="radio" :value="true" />
                        <div class="radio-card-body">
                            <strong>Namen tonen</strong>
                            <small class="muted">Deelnemers zien elkaars naam en wat ze gestemd hebben.</small>
                        </div>
                    </label>
                    <label class="radio-card" :class="{ 'is-selected': formData.show_voter_names === false }">
                        <input v-model="formData.show_voter_names" :name="htmlFieldNames ? 'show_voter_names' : null" type="radio" :value="false" />
                        <div class="radio-card-body">
                            <strong>Namen verbergen</strong>
                            <small class="muted">Deelnemers zien alleen de aantallen, niet wie er hoe gestemd heeft.</small>
                        </div>
                    </label>
                </div>
            </div>

            <!-- Response mode -->
            <div class="form-field">
                <span>Stemopties</span>
                <div class="radio-card-group">
                    <label class="radio-card" :class="{ 'is-selected': formData.response_mode === 'yes_maybe_no' }">
                        <input v-model="formData.response_mode" :name="htmlFieldNames ? 'response_mode' : null" type="radio" value="yes_maybe_no" />
                        <div class="radio-card-body">
                            <strong>Ja / Misschien / Nee</strong>
                            <small class="muted">Deelnemers kunnen ook twijfelen — handig als je rekening wil houden met onzekerheid.</small>
                        </div>
                    </label>
                    <label class="radio-card" :class="{ 'is-selected': formData.response_mode === 'yes_no' }">
                        <input v-model="formData.response_mode" :name="htmlFieldNames ? 'response_mode' : null" type="radio" value="yes_no" />
                        <div class="radio-card-body">
                            <strong>Ja / Nee</strong>
                            <small class="muted">Eenvoudiger: deelnemers kiezen simpelweg wel of niet.</small>
                        </div>
                    </label>
                </div>
            </div>

            <!-- Selection mode -->
            <div class="form-field">
                <span>Hoeveel datums mag iemand positief kiezen?</span>
                <div class="radio-card-group">
                    <label class="radio-card" :class="{ 'is-selected': formData.selection_mode === 'multiple' }">
                        <input v-model="formData.selection_mode" :name="htmlFieldNames ? 'selection_mode' : null" type="radio" value="multiple" />
                        <div class="radio-card-body">
                            <strong>Meerdere datums</strong>
                            <small class="muted">Deelnemers kunnen op alle datums stemmen die hen passen.</small>
                        </div>
                    </label>
                    <label class="radio-card" :class="{ 'is-selected': formData.selection_mode === 'single' }">
                        <input v-model="formData.selection_mode" :name="htmlFieldNames ? 'selection_mode' : null" type="radio" value="single" />
                        <div class="radio-card-body">
                            <strong>Slechts 1 datum</strong>
                            <small class="muted">Deelnemers kiezen de datum die hen het best uitkomt.</small>
                        </div>
                    </label>
                </div>
                <div v-if="formData.selection_mode === 'multiple'" class="form-field-row section-spacing-top-xs max-w-260">
                    <label class="form-field">
                        Max. positieve keuzes (optioneel)
                        <input v-model="formData.max_selections" :name="htmlFieldNames ? 'max_selections' : null" type="number" min="1" max="8" placeholder="Geen limiet" />
                    </label>
                </div>
            </div>
        </section>

        <!-- ── Stap 4: Deelnemers ────────────────────────────── -->
        <section v-if="step === 4" class="poll-wizard-panel">
            <div class="step-intro">
                <strong class="step-intro-title">Stap 4 — Deelnemers uitnodigen</strong>
                <p class="muted text-sm m-0">Nodig mensen uit om te stemmen. Je kunt dit ook later nog doen nadat je de prikker hebt aangemaakt.</p>
            </div>

            <!-- Tag picker -->
            <div v-if="showTagPicker && !isLightAccount && availableTags.length" class="form-field">
                <span>Nodig uit via tags</span>
                <div class="tag-checkbox-group">
                    <label
                        v-for="tag in availableTags.slice(0, 16)"
                        :key="`poll-tag-${tag.id}`"
                        class="tag-checkbox-item"
                    >
                        <input v-model="formData.participant_tag_ids" :name="htmlFieldNames ? 'participant_tag_ids[]' : null" type="checkbox" :value="tag.id" />
                        <span class="pill" :style="{ background: tag.color || 'var(--mint)' }">{{ tag.name }}</span>
                    </label>
                </div>
            </div>

            <!-- Contact quick-add -->
            <div v-if="showContactSuggestions && (pollContacts.length || lightContacts.length)" class="form-field">
                <span>Snel toevoegen uit contacten</span>
                <div class="contact-quickadd-wrapper">
                    <input
                        v-model="contactSearch"
                        class="contact-search-input"
                        type="search"
                        placeholder="Zoeken op naam of e-mail…"
                    />
                    <div class="contact-quickadd-list">
                        <button
                            v-for="contact in filteredQuickAddContacts"
                            :key="`qc-${contact.email}`"
                            class="contact-quickadd-row"
                            :class="{ 'is-added': isParticipantAdded(contact.email) }"
                            type="button"
                            @click="addParticipantFromContact(contact)"
                        >
                            <span class="contact-quickadd-check">{{ isParticipantAdded(contact.email) ? '✓' : '+' }}</span>
                            <span class="contact-quickadd-name">{{ contact.name || contact.email }}</span>
                            <span v-if="contact.name && contact.email" class="contact-quickadd-email muted">{{ contact.email }}</span>
                            <span v-if="contact._light" class="contact-quickadd-tag muted text-xs">(via prikker)</span>
                        </button>
                        <div v-if="filteredQuickAddContacts.length === 0" class="contact-quickadd-empty muted text-sm">
                            Geen contacten gevonden.
                        </div>
                    </div>
                </div>
            </div>

            <!-- Manual participant rows -->
            <div class="form-field">
                <span>Nieuwe contacten toevoegen</span>
                <div v-if="formData.participants.length" class="participants-list">
                    <div class="participants-list-head">
                        <span>Naam</span>
                        <span>E-mailadres</span>
                        <span></span>
                    </div>
                    <div
                        v-for="(participant, idx) in formData.participants"
                        :key="`participant-${idx}`"
                        class="participant-row"
                    >
                        <input v-model="participant.name" :name="htmlFieldNames ? `participants[${idx}][name]` : null" class="form-input-standalone" type="text" placeholder="Naam" />
                        <input v-model="participant.email" :name="htmlFieldNames ? `participants[${idx}][email]` : null" class="form-input-standalone" type="email" placeholder="E-mailadres" />
                        <button class="button button-secondary button--xs btn-remove-participant" type="button" @click="removeParticipant(idx)" title="Verwijder">×</button>
                    </div>
                </div>
                <button class="button button-secondary button--xs section-spacing-top-xs" type="button" @click="addParticipant">+ Deelnemer toevoegen</button>
            </div>

            <!-- Summary badge -->
            <div v-if="totalInviteCount > 0" class="invite-summary">
                <span class="pill invite-summary-pill">
                    {{ totalInviteCount }} {{ totalInviteCount === 1 ? 'deelnemer' : 'deelnemers' }} uitgenodigd
                </span>
            </div>
        </section>

        <!-- Global errors -->
        <div v-if="step !== 2 && mergedErrors.options" class="error-box">{{ mergedErrors.options }}</div>
        <div v-if="step === 1 && mergedErrors.title" class="error-box">{{ mergedErrors.title }}</div>

        <!-- Navigation -->
        <div class="poll-wizard-actions">
            <div v-if="step === 4" class="poll-wizard-actions-hint">
                Je kunt deelnemers ook later nog uitnodigen.
            </div>
            <div class="poll-wizard-actions-row">
                <div class="poll-wizard-actions-left">
                    <button v-if="step === 1" class="button button-secondary" type="button" @click="emit('cancel')">
                        Annuleren
                    </button>
                    <button v-else class="button button-secondary" type="button" @click="step -= 1">
                        Vorige stap
                    </button>
                </div>
                <div class="poll-wizard-actions-right">
                    <button
                        v-if="step < 4"
                        class="button button-primary"
                        type="button"
                        :disabled="step === 2 && !canAdvanceFromStepTwo"
                        @click="goToNextStep"
                    >
                        Volgende stap
                    </button>
                    <button v-else class="button button-primary" type="button" :disabled="processing" @click="submitWizard">
                        {{ editing ? 'Wijzigingen opslaan' : 'Datumprikker aanmaken' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'

const props = defineProps({
    formData: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    processing: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
    htmlFieldNames: { type: Boolean, default: false },
    useDateModal: { type: Boolean, default: true },
    showCreatorFields: { type: Boolean, default: false },
    creatorLocked: { type: Boolean, default: false },
    creatorName: { type: String, default: '' },
    creatorEmail: { type: String, default: '' },
    showTagPicker: { type: Boolean, default: true },
    showContactSuggestions: { type: Boolean, default: true },
    availableTags: { type: Array, default: () => [] },
    pollContacts: { type: Array, default: () => [] },
    lightContacts: { type: Array, default: () => [] },
    isLightAccount: { type: Boolean, default: false },
    formatReadableDate: {
        type: Function,
        default: (value) => value || '-',
    },
})

const emit = defineEmits(['submit', 'cancel', 'openDateModal', 'openCloseDateModal'])

const TOTAL_STEPS = 4

const step = ref(1)
const fixedOptionSlots = ref(['', '', '', '', '', ''])
const localCreatorNameError = ref('')
const localCreatorEmailError = ref('')
const localTitleError = ref('')
const localOptionsError = ref('')
const contactSearch = ref('')
const showApplyConfirm = ref(false)

const wizardProgress = computed(() => Math.round((step.value / TOTAL_STEPS) * 100))

const uniqueSelectedDates = computed(() => {
    const values = Array.isArray(props.formData.options)
        ? props.formData.options.filter(Boolean)
        : []
    return [...new Set(values)]
})

const sortedSelectedDates = computed(() =>
    [...uniqueSelectedDates.value].sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
)

const firstDateTimeValue = computed(() => {
    const firstDate = sortedSelectedDates.value[0]
    if (!firstDate) return ''
    return String(props.formData.option_times?.[firstDate] || '')
})

const canApplyFirstTimeToAll = computed(() =>
    sortedSelectedDates.value.length > 1 && firstDateTimeValue.value !== ''
)

const canAdvanceFromStepTwo = computed(() => uniqueSelectedDates.value.length >= 2)

const mergedErrors = computed(() => ({
    creator_name: localCreatorNameError.value || props.errors?.creator_name || '',
    creator_email: localCreatorEmailError.value || props.errors?.creator_email || '',
    title: localTitleError.value || props.errors?.title || '',
    options: localOptionsError.value || props.errors?.options || '',
}))

const addedEmails = computed(() =>
    new Set(
        (props.formData.participants || [])
            .map((p) => String(p.email || '').trim().toLowerCase())
            .filter(Boolean)
    )
)

const allQuickAddContacts = computed(() => {
    const contacts = [
        ...props.pollContacts.map((c) => ({ ...c, _light: false })),
        ...props.lightContacts.map((c) => ({ ...c, _light: true })),
    ]
    return contacts
        .filter((c) => c.email)
        .sort((a, b) => {
            const nameA = (a.name || a.email).toLowerCase()
            const nameB = (b.name || b.email).toLowerCase()
            return nameA.localeCompare(nameB, 'nl')
        })
})

const filteredQuickAddContacts = computed(() => {
    const q = contactSearch.value.trim().toLowerCase()
    if (!q) return allQuickAddContacts.value
    return allQuickAddContacts.value.filter((c) => {
        return (
            (c.name || '').toLowerCase().includes(q) ||
            (c.email || '').toLowerCase().includes(q)
        )
    })
})

const isParticipantAdded = (email) => addedEmails.value.has(String(email || '').trim().toLowerCase())

const totalInviteCount = computed(() => {
    let count = 0
    // tag-based participants are not enumerable here, only manual rows
    count += (props.formData.participants || []).filter((p) => p.name || p.email).length
    count += (props.formData.participant_tag_ids || []).length
    return count
})

const ensureDefaults = () => {
    const defaults = {
        title: '',
        description: '',
        options: [],
        closes_at: '',
        selection_mode: 'multiple',
        visibility_mode: 'public_results',
        show_voter_names: true,
        response_mode: 'yes_maybe_no',
        max_selections: '',
        participants: [],
        participant_tag_ids: [],
        option_times: {},
    }

    Object.keys(defaults).forEach((key) => {
        if (typeof props.formData[key] === 'undefined' || props.formData[key] === null) {
            props.formData[key] = Array.isArray(defaults[key]) ? [...defaults[key]] : defaults[key]
        }
    })

    if (!props.useDateModal) {
        const options = Array.isArray(props.formData.options) ? props.formData.options : []
        fixedOptionSlots.value = Array.from({ length: 6 }, (_, index) => options[index] || '')
    }
}

const clearCoreErrors = () => {
    localCreatorNameError.value = ''
    localCreatorEmailError.value = ''
    localTitleError.value = ''
    localOptionsError.value = ''

    if (typeof props.formData.clearErrors === 'function') {
        props.formData.clearErrors('creator_name')
        props.formData.clearErrors('creator_email')
        props.formData.clearErrors('options')
        props.formData.clearErrors('title')
    }
}

const setError = (field, message) => {
    if (field === 'title') localTitleError.value = message
    if (field === 'options') localOptionsError.value = message
    if (field === 'creator_name') localCreatorNameError.value = message
    if (field === 'creator_email') localCreatorEmailError.value = message

    if (typeof props.formData.setError === 'function') {
        props.formData.setError(field, message)
    }
}

const validateStep = () => {
    clearCoreErrors()

    if (step.value === 1 && props.showCreatorFields && !props.creatorLocked) {
        if (!String(props.formData.creator_name || '').trim()) {
            setError('creator_name', 'Vul je naam in om door te gaan.')
            return false
        }
        const email = String(props.formData.creator_email || '').trim()
        if (!email) {
            setError('creator_email', 'Vul je e-mailadres in om door te gaan.')
            return false
        }
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailPattern.test(email)) {
            setError('creator_email', 'Vul een geldig e-mailadres in.')
            return false
        }
    }

    if (step.value === 1 && !String(props.formData.title || '').trim()) {
        setError('title', 'Vul een titel in om door te gaan.')
        return false
    }

    if (step.value === 2 && uniqueSelectedDates.value.length < 2) {
        setError('options', 'Voeg minimaal twee verschillende datums toe.')
        return false
    }

    return true
}

const goToStep = (targetStep) => {
    const next = Math.min(Math.max(targetStep, 1), TOTAL_STEPS)
    if (next > step.value && !validateStep()) return
    step.value = next
}

const goToNextStep = () => {
    if (!validateStep()) return
    step.value = Math.min(step.value + 1, TOTAL_STEPS)
}

const normalizeParticipants = () => {
    if (!Array.isArray(props.formData.participants)) {
        props.formData.participants = []
        return
    }
    props.formData.participants = props.formData.participants.filter((p) => p && (p.name || p.email))
}

const submitWizard = () => {
    if (!validateStep()) return
    normalizeParticipants()
    emit('submit')
}

const addParticipant = () => {
    props.formData.participants.push({ name: '', email: '' })
}

const removeParticipant = (index) => {
    props.formData.participants.splice(index, 1)
}

const addParticipantFromContact = (contact) => {
    const email = String(contact?.email || '').trim().toLowerCase()
    if (!email) return
    if (isParticipantAdded(email)) return
    props.formData.participants.push({
        name: contact.name || '',
        email,
    })
}

const syncFixedOptionSlots = () => {
    props.formData.options = fixedOptionSlots.value.filter(Boolean)
}

const applyFirstTimeToAllDates = () => {
    const baseTime = firstDateTimeValue.value
    if (!baseTime) return
    sortedSelectedDates.value.forEach((date) => {
        props.formData.option_times[date] = baseTime
    })
}

const confirmApplyTime = () => {
    applyFirstTimeToAllDates()
    showApplyConfirm.value = false
}

const formatDisplayDate = (value) => {
    if (!value) return ''
    return new Date(value).toLocaleDateString('nl-NL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
}

onMounted(() => {
    ensureDefaults()
})

watch(firstDateTimeValue, () => {
    showApplyConfirm.value = false
})

watch(sortedSelectedDates, (dates) => {
    const allowed = new Set(dates)
    const next = {}
    Object.entries(props.formData.option_times || {}).forEach(([date, value]) => {
        if (allowed.has(date) && value) next[date] = value
    })
    props.formData.option_times = next
}, { immediate: true })

defineExpose({
    resetWizard: () => {
        step.value = 1
    },
})
</script>

<style scoped>
/* ── Wizard wrapper ── */
.poll-create-wizard {
    display: grid;
    gap: 20px;
}

/* ── Step pills ── */
.poll-wizard-steps {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
}

.step-pill-arrow {
    color: rgba(100, 116, 139, 0.4);
    font-size: 1rem;
    line-height: 1;
    flex-shrink: 0;
}

.poll-wizard-step-pill {
    display: flex;
    align-items: center;
    gap: 5px;
    border: 1px solid rgba(100, 116, 139, 0.25);
    background: #fff;
    color: #64748b;
    border-radius: 999px;
    padding: 4px 10px 4px 6px;
    font-size: 0.8rem;
    line-height: 1.2;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.step-pill-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(100, 116, 139, 0.12);
    font-size: 0.72rem;
    font-weight: 600;
    flex-shrink: 0;
}

.poll-wizard-step-pill.is-active {
    border-color: rgba(242, 139, 115, 0.7);
    color: #9a3412;
    background: rgba(255, 237, 213, 0.7);
}

.poll-wizard-step-pill.is-active .step-pill-num {
    background: rgba(242, 139, 115, 0.35);
}

.poll-wizard-step-pill.is-done {
    border-color: rgba(100, 116, 139, 0.3);
    color: #475569;
}

.poll-wizard-step-pill.is-done .step-pill-num {
    background: rgba(100, 116, 139, 0.15);
}

/* ── Progress bar ── */
.poll-wizard-progress-bar {
    width: 100%;
    height: 5px;
    border-radius: 999px;
    overflow: hidden;
    background: rgba(148, 163, 184, 0.22);
    margin-top: -10px;
}

.poll-wizard-progress-fill {
    display: block;
    height: 100%;
    background: var(--coral, #f28b73);
    transition: width 0.25s ease;
}

/* ── Panel ── */
.poll-wizard-panel {
    display: grid;
    gap: 20px;
}

/* ── Step intro ── */
.step-intro {
    display: grid;
    gap: 4px;
}

.step-intro-title {
    font-size: 1rem;
    color: #1e293b;
}

/* ── Form rows ── */
.form-field-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
}

/* ── Dates header ── */
.poll-dates-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
}

/* ── Dates table ── */
.poll-dates-table {
    display: grid;
    gap: 0;
    border: 1px solid rgba(100, 116, 139, 0.18);
    border-radius: var(--radius-md, 8px);
    overflow: hidden;
}

.poll-dates-table-head {
    display: grid;
    grid-template-columns: 1fr auto 100px;
    gap: 0;
    background: rgba(148, 163, 184, 0.1);
    padding: 7px 14px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.poll-dates-table-row {
    display: grid;
    grid-template-columns: 1fr auto 100px;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-top: 1px solid rgba(100, 116, 139, 0.12);
}

.poll-dates-table-row:hover {
    background: rgba(148, 163, 184, 0.06);
}

.poll-dates-table-date {
    font-size: 0.88rem;
    color: #1e293b;
    font-weight: 500;
}

.poll-dates-table-time .time-input {
    border: 1px solid rgba(100, 116, 139, 0.2);
    border-radius: var(--radius-sm, 5px);
    padding: 4px 8px;
    font: inherit;
    font-size: 0.88rem;
    background: #fff;
    width: 100%;
    max-width: 120px;
}

.poll-dates-table-time .time-input:focus {
    outline: none;
    border-color: var(--coral);
    box-shadow: 0 0 0 3px rgba(var(--coral-rgb), 0.12);
}

.poll-dates-table-apply {
    width: 100px;
    display: flex;
    justify-content: flex-end;
}

.btn-apply-time {
    font-size: 0.75rem;
    padding: 3px 8px;
    white-space: nowrap;
}

.poll-dates-empty {
    padding: 16px 14px;
    border: 1px dashed rgba(100, 116, 139, 0.25);
    border-radius: var(--radius-md, 8px);
}

/* ── Inline time-apply confirm panel ── */
.poll-time-confirm-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 14px;
    border-top: 1px solid rgba(100, 116, 139, 0.14);
    background: rgba(255, 237, 213, 0.45);
    border-bottom-left-radius: var(--radius-md, 8px);
    border-bottom-right-radius: var(--radius-md, 8px);
}

.poll-time-confirm-text {
    font-size: 0.85rem;
    color: #1e293b;
    line-height: 1.4;
}

.poll-time-confirm-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

/* ── Closing date ── */
.date-picker-trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
    flex-wrap: wrap;
}

.closes-at-display {
    font-size: 0.88rem;
}

/* ── Ghost button variant ── */
.button-ghost {
    background: transparent;
    border: 1px solid rgba(100, 116, 139, 0.3);
    color: #475569;
    transition: border-color 0.15s, background 0.15s;
}

.button-ghost:hover {
    background: rgba(100, 116, 139, 0.07);
    border-color: rgba(100, 116, 139, 0.45);
}

/* ── Radio cards (step 3) ── */
.radio-card-group {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 8px;
    margin-top: 6px;
}

.radio-card {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    border: 1px solid rgba(100, 116, 139, 0.22);
    border-radius: var(--radius-md, 8px);
    padding: 12px 14px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    background: #fff;
}

.radio-card input[type="radio"] {
    margin-top: 3px;
    flex-shrink: 0;
    accent-color: var(--coral, #f28b73);
}

.radio-card.is-selected {
    border-color: rgba(242, 139, 115, 0.6);
    background: rgba(255, 237, 213, 0.45);
}

.radio-card-body {
    display: grid;
    gap: 2px;
}

.radio-card-body strong {
    font-size: 0.88rem;
    color: #1e293b;
}

.radio-card-body small {
    font-size: 0.78rem;
    line-height: 1.4;
    display: block;
}

/* ── Tag checkboxes (step 4) ── */
.tag-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
}

.tag-checkbox-item {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
}

/* ── Contact quick-add (step 4) ── */
.contact-quickadd-wrapper {
    margin-top: 8px;
    display: grid;
    gap: 8px;
}

.contact-search-input {
    border: 1px solid rgba(100, 116, 139, 0.25);
    border-radius: var(--radius-md, 8px);
    padding: 7px 12px;
    font: inherit;
    font-size: 0.88rem;
    width: 100%;
    background: #fff;
    transition: border-color 0.15s;
}

.contact-search-input:focus {
    outline: none;
    border-color: var(--coral);
    box-shadow: 0 0 0 3px rgba(var(--coral-rgb), 0.12);
}

.contact-quickadd-list {
    border: 1px solid rgba(100, 116, 139, 0.18);
    border-radius: var(--radius-md, 8px);
    overflow: hidden;
    max-height: 220px;
    overflow-y: auto;
}

.contact-quickadd-row {
    display: grid;
    grid-template-columns: 20px 1fr auto auto;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 9px 12px;
    border: none;
    border-top: 1px solid rgba(100, 116, 139, 0.1);
    background: #fff;
    text-align: left;
    cursor: pointer;
    font: inherit;
    font-size: 0.875rem;
    transition: background 0.12s;
}

.contact-quickadd-row:first-child {
    border-top: none;
}

.contact-quickadd-row:hover:not(.is-added) {
    background: rgba(var(--coral-rgb), 0.05);
}

.contact-quickadd-row.is-added {
    background: rgba(var(--mint-rgb, 160, 220, 180), 0.12);
    cursor: default;
    color: #475569;
}

.contact-quickadd-check {
    font-size: 0.8rem;
    color: #94a3b8;
    font-weight: 600;
    text-align: center;
}

.contact-quickadd-row.is-added .contact-quickadd-check {
    color: #16a34a;
}

.contact-quickadd-name {
    font-weight: 500;
    color: #1e293b;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.contact-quickadd-row.is-added .contact-quickadd-name {
    color: #475569;
}

.contact-quickadd-email {
    font-size: 0.8rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.contact-quickadd-tag {
    white-space: nowrap;
}

.contact-quickadd-empty {
    padding: 14px 12px;
    text-align: center;
}

/* ── Participants list (step 4) ── */
.participants-list {
    display: grid;
    gap: 0;
    border: 1px solid rgba(100, 116, 139, 0.18);
    border-radius: var(--radius-md, 8px);
    overflow: hidden;
    margin-top: 6px;
}

.participants-list-head {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 10px;
    background: rgba(148, 163, 184, 0.1);
    padding: 7px 14px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.participant-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-top: 1px solid rgba(100, 116, 139, 0.12);
}

.participant-row:hover {
    background: rgba(148, 163, 184, 0.05);
}

.form-input-standalone {
    border: 1px solid rgba(var(--coral-rgb), 0.2);
    border-radius: var(--radius-md);
    padding: var(--sp-1) var(--sp-1-5);
    background: #fff;
    font: inherit;
    width: 100%;
}

.form-input-standalone:focus {
    outline: none;
    border-color: var(--coral);
    box-shadow: 0 0 0 3px rgba(var(--coral-rgb), 0.15);
}

.btn-remove-participant {
    padding: 4px 8px;
    font-size: 1rem;
    line-height: 1;
}

/* ── Invite summary ── */
.invite-summary {
    display: flex;
    align-items: center;
    gap: 8px;
}

.invite-summary-pill {
    background: rgba(var(--mint-rgb, 160, 220, 180), 0.35);
}

/* ── Actions ── */
.poll-wizard-actions {
    display: grid;
    gap: 10px;
}

.poll-wizard-actions-hint {
    font-size: 0.82rem;
    color: #64748b;
    text-align: center;
}

.poll-wizard-actions-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.poll-wizard-actions-left,
.poll-wizard-actions-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* ── Mobile ── */
@media (max-width: 520px) {
    .poll-dates-table-head,
    .poll-dates-table-row {
        grid-template-columns: 1fr auto;
        gap: 8px;
    }

    .poll-dates-table-head span:last-child,
    .poll-dates-table-apply {
        display: none;
    }

    .participants-list-head {
        display: none;
    }

    .participant-row {
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto;
    }

    .participant-row .form-input-standalone:first-child {
        grid-column: 1;
        grid-row: 1;
    }

    .participant-row .form-input-standalone:nth-child(2) {
        grid-column: 1;
        grid-row: 2;
    }

    .participant-row .btn-remove-participant {
        grid-column: 2;
        grid-row: 1 / span 2;
        align-self: center;
    }

    .step-pill-label {
        display: none;
    }

    .poll-wizard-step-pill {
        padding: 4px 6px;
    }

    .contact-quickadd-row {
        grid-template-columns: 20px 1fr;
    }

    .contact-quickadd-email,
    .contact-quickadd-tag {
        display: none;
    }
}
</style>
